import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";

import { Context } from "../../../index";
import AccountService from "../../../services/AccountService";
import { accountStateTitle } from "../accountState";
import { serverMessageOf } from "../apiError";
import { useCabinetRefresh } from "../CabinetRefresh";
import { CabinetEmpty, CabinetSkeleton } from "../components/CabinetStates";
import { formatDate, formatFullName } from "../format";
import CabinetProfileApi, { MyConsent } from "../services/CabinetProfileApi";
import {
  passwordErrorOf,
  PASSWORD_MIN_LENGTH,
  ProfileFormValues,
  profilePayloadOf,
} from "../profileForm";
import { SUPPORT_LINK } from "../support";
import type { MyPlace } from "../types";

/**
 * `Ф3` (кабинет ПЗС): профиль.
 *
 * Пять предметов в одном разделе, у каждого свой шов:
 * - личные данные (`PATCH /my/profile`) — почта только чтение (`Л9`);
 * - мои машиноместа (`GET /my/places`) — станция подключена / ждёт акта /
 *   связь закрыта с датой «история до» (`Л5`);
 * - смена пароля (`POST /my/password`) — сообщение сервера при неверном старом;
 * - согласие (`GET /my/consent`) — редакция, дата и принятый текст (`Л6`);
 * - удаление учётки (`POST /account/deletion` → код → `/confirm`) (`ЖЦ-03/04`).
 */

type Notice = { tone: "ok" | "error"; text: string } | null;

function NoticeLine({ notice }: { notice: Notice }) {
  if (!notice) return null;

  return (
    <p
      className={`cabinet-notice cabinet-notice--${notice.tone}`}
      role="status"
    >
      {notice.text}
    </p>
  );
}

function PersonalDataSection() {
  const { store } = useContext(Context);
  const user = store?.user;
  const [values, setValues] = useState<ProfileFormValues>({
    lastName: user?.lastName || "",
    firstName: user?.firstName || "",
    surName: user?.surName || "",
    tel: user?.tel || "",
  });
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);

  const change =
    (field: keyof ProfileFormValues) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setValues((current) => ({ ...current, [field]: event.target.value }));
      setNotice(null);
    };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setNotice(null);

    try {
      // Только правимые поля: почта, роль и состояние в тело не попадают.
      await CabinetProfileApi.updateProfile(profilePayloadOf(values));
      await store.loadMyProfile();
      setNotice({ tone: "ok", text: "Данные сохранены" });
    } catch (error) {
      setNotice({ tone: "error", text: serverMessageOf(error) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="cabinet-card" aria-labelledby="cabinet-personal-title">
      <h2 id="cabinet-personal-title" className="cabinet-card__title">
        Личные данные
      </h2>

      <form className="cabinet-form" onSubmit={save}>
        <div className="cabinet-form__grid">
          <label className="cabinet-field">
            <span>Фамилия</span>
            <input
              name="lastName"
              value={values.lastName}
              onChange={change("lastName")}
              autoComplete="family-name"
            />
          </label>
          <label className="cabinet-field">
            <span>Имя</span>
            <input
              name="firstName"
              value={values.firstName}
              onChange={change("firstName")}
              autoComplete="given-name"
            />
          </label>
          <label className="cabinet-field">
            <span>Отчество</span>
            <input
              name="surName"
              value={values.surName}
              onChange={change("surName")}
              autoComplete="additional-name"
            />
          </label>
          <label className="cabinet-field">
            <span>Телефон</span>
            <input
              name="tel"
              value={values.tel}
              onChange={change("tel")}
              autoComplete="tel"
              inputMode="tel"
            />
          </label>
        </div>

        <div className="cabinet-field cabinet-field--readonly">
          <span>Почта</span>
          <span className="cabinet-field__value">{user?.email || "—"}</span>
          <a href={SUPPORT_LINK}>написать в поддержку</a>
        </div>

        <p className="cabinet-hint">
          Состояние учётной записи: {accountStateTitle(user?.accountState)}.
          Почта меняется только через поддержку.
        </p>

        <NoticeLine notice={notice} />

        <div className="cabinet-form__actions">
          <button
            type="submit"
            className="cabinet-btn cabinet-btn--primary"
            disabled={saving}
          >
            {saving ? "Сохраняем…" : "Сохранить"}
          </button>
        </div>
      </form>
    </section>
  );
}

function placeStateOf(place: MyPlace): { label: string; note: string | null } {
  if (place.state === "closed" || place.state === "cancelled") {
    return {
      label: "связь закрыта",
      note: `история доступна до ${formatDate(place.history_until)}`,
    };
  }

  if (place.state === "pending") {
    return {
      label: "связь ещё не началась",
      note: `начало — ${formatDate(place.started_at)}`,
    };
  }

  return place.station_connected
    ? { label: "станция подключена", note: null }
    : { label: "ожидает акта", note: "данные станции появятся после акта" };
}

function PlacesSection({
  places,
  loading,
  error,
  onRetry,
}: {
  places: MyPlace[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
}) {
  return (
    <section className="cabinet-card" aria-labelledby="cabinet-places-title">
      <h2 id="cabinet-places-title" className="cabinet-card__title">
        Мои машиноместа
      </h2>

      {loading && places.length === 0 ? <CabinetSkeleton rows={2} /> : null}

      {!loading && error && places.length === 0 ? (
        <CabinetEmpty title="Не удалось загрузить места">
          <button type="button" className="cabinet-btn" onClick={onRetry}>
            Обновить
          </button>
        </CabinetEmpty>
      ) : null}

      {!loading && !error && places.length === 0 ? (
        <CabinetEmpty title="Машиномест пока нет">
          Места появляются после заключения договора —{" "}
          <a href={SUPPORT_LINK}>напишите в поддержку</a>.
        </CabinetEmpty>
      ) : null}

      {places.length > 0 ? (
        <ul className="cabinet-place-list">
          {places.map((place) => {
            const state = placeStateOf(place);

            return (
              <li key={place.link_id} className="cabinet-place-row">
                <div className="cabinet-place-row__main">
                  <b>Место {place.place?.number ?? "—"}</b>
                  <span>{place.place?.plant?.name ?? "—"}</span>
                </div>
                <div className="cabinet-place-row__contract">
                  <span className="cabinet-hint">Договор</span>
                  <span>
                    {place.contract?.number
                      ? `№ ${place.contract.number}${
                          place.contract.date
                            ? ` от ${formatDate(place.contract.date)}`
                            : ""
                        }`
                      : "—"}
                  </span>
                </div>
                <div className="cabinet-place-row__state">
                  <span className="cabinet-place__chip">{state.label}</span>
                  {state.note ? (
                    <span className="cabinet-hint">{state.note}</span>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}
    </section>
  );
}

function PasswordSection() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const error = passwordErrorOf({
      currentPassword,
      newPassword,
      repeatPassword,
    });

    if (error) {
      setNotice({ tone: "error", text: error });
      return;
    }

    setSaving(true);
    setNotice(null);

    try {
      await CabinetProfileApi.changePassword({ currentPassword, newPassword });
      setCurrentPassword("");
      setNewPassword("");
      setRepeatPassword("");
      setNotice({ tone: "ok", text: "Пароль изменён" });
    } catch (serverError) {
      setNotice({ tone: "error", text: serverMessageOf(serverError) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="cabinet-card" aria-labelledby="cabinet-password-title">
      <h2 id="cabinet-password-title" className="cabinet-card__title">
        Смена пароля
      </h2>

      <form className="cabinet-form" onSubmit={submit}>
        <div className="cabinet-form__grid">
          <label className="cabinet-field">
            <span>Текущий пароль</span>
            <input
              type="password"
              name="currentPassword"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              autoComplete="current-password"
            />
          </label>
          <label className="cabinet-field">
            <span>Новый пароль</span>
            <input
              type="password"
              name="newPassword"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              autoComplete="new-password"
            />
          </label>
          <label className="cabinet-field">
            <span>Новый пароль ещё раз</span>
            <input
              type="password"
              name="repeatPassword"
              value={repeatPassword}
              onChange={(event) => setRepeatPassword(event.target.value)}
              autoComplete="new-password"
            />
          </label>
        </div>

        <p className="cabinet-hint">
          Не короче {PASSWORD_MIN_LENGTH} знаков. После смены прочие сессии
          закрываются, текущая остаётся.
        </p>

        <NoticeLine notice={notice} />

        <div className="cabinet-form__actions">
          <button
            type="submit"
            className="cabinet-btn cabinet-btn--primary"
            disabled={saving}
          >
            {saving ? "Меняем…" : "Сменить пароль"}
          </button>
        </div>
      </form>
    </section>
  );
}

function ConsentSection({
  consent,
  loading,
  error,
  onRetry,
}: {
  consent: MyConsent | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <section className="cabinet-card" aria-labelledby="cabinet-consent-title">
      <h2 id="cabinet-consent-title" className="cabinet-card__title">
        Согласие на обработку персональных данных
      </h2>

      {loading && !consent ? <CabinetSkeleton rows={1} /> : null}

      {!loading && error && !consent ? (
        <CabinetEmpty title="Не удалось загрузить согласие">
          <button type="button" className="cabinet-btn" onClick={onRetry}>
            Обновить
          </button>
        </CabinetEmpty>
      ) : null}

      {consent && !consent.has_consent ? (
        <p className="cabinet-hint">Согласие не принято.</p>
      ) : null}

      {consent?.has_consent ? (
        <div className="cabinet-consent">
          <p className="cabinet-hint">
            Редакция {consent.version ?? "—"} · принято{" "}
            {formatDate(consent.accepted_at)}
          </p>
          {consent.has_newer_version ? (
            <p className="cabinet-notice cabinet-notice--ok" role="status">
              Есть новая редакция согласия. Пока она не принята, действует
              принятая.
            </p>
          ) : null}
          <div className="cabinet-form__actions">
            <button
              type="button"
              className="cabinet-btn"
              onClick={() => setOpen((value) => !value)}
              aria-expanded={open}
            >
              {open ? "Скрыть текст" : "Открыть принятый текст"}
            </button>
          </div>
          {open && consent.text ? (
            <div className="cabinet-consent-text">{consent.text}</div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function DeletionSection({ accountState }: { accountState?: string | null }) {
  const { store } = useContext(Context);
  const [step, setStep] = useState<"idle" | "code" | "done">("idle");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);

  const request = async () => {
    setBusy(true);
    setNotice(null);

    try {
      await AccountService.requestDeletion();
      setStep("code");
      setNotice({
        tone: "ok",
        text: "Код подтверждения отправлен на почту из профиля.",
      });
    } catch (error) {
      setNotice({ tone: "error", text: serverMessageOf(error) });
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setNotice(null);

    try {
      await AccountService.confirmDeletion(code.trim());
      // Состояние и срок — с сервера: плашка Ф0 покажет дату.
      await store.loadMyProfile();
      setStep("done");
      setCode("");
      setNotice({
        tone: "ok",
        text: "Учётная запись поставлена на удаление. Срок и отмена — в плашке выше.",
      });
    } catch (error) {
      setNotice({ tone: "error", text: serverMessageOf(error) });
    } finally {
      setBusy(false);
    }
  };

  const alreadyRequested = accountState === "deletion_requested";

  return (
    <section className="cabinet-card" aria-labelledby="cabinet-deletion-title">
      <h2 id="cabinet-deletion-title" className="cabinet-card__title">
        Удаление учётной записи
      </h2>

      {alreadyRequested ? (
        <p className="cabinet-hint">
          Заявка на удаление подана. Отменить её можно в плашке над содержимым.
        </p>
      ) : null}

      {!alreadyRequested && step === "idle" ? (
        <>
          <p className="cabinet-hint">
            Удаление сработает не сразу: после подтверждения останется срок, в
            который его можно отменить.
          </p>
          <NoticeLine notice={notice} />
          <div className="cabinet-form__actions">
            <button
              type="button"
              className="cabinet-btn"
              onClick={request}
              disabled={busy}
            >
              {busy ? "Отправляем код…" : "Запросить удаление"}
            </button>
          </div>
        </>
      ) : null}

      {!alreadyRequested && step === "code" ? (
        <form className="cabinet-form" onSubmit={confirm}>
          <label className="cabinet-field">
            <span>Код из письма</span>
            <input
              name="code"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              inputMode="numeric"
              autoComplete="one-time-code"
            />
          </label>

          <NoticeLine notice={notice} />

          <div className="cabinet-form__actions">
            <button
              type="submit"
              className="cabinet-btn cabinet-btn--primary"
              disabled={busy || code.trim() === ""}
            >
              {busy ? "Подтверждаем…" : "Подтвердить удаление"}
            </button>
          </div>
        </form>
      ) : null}

      {!alreadyRequested && step === "done" ? <NoticeLine notice={notice} /> : null}
    </section>
  );
}

function CabinetProfilePage() {
  const { store } = useContext(Context);
  const user = store?.user;
  const { version, markUpdated, markFailed } = useCabinetRefresh();
  const [places, setPlaces] = useState<MyPlace[]>([]);
  const [consent, setConsent] = useState<MyConsent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const reload = useCallback(() => setReloadKey((value) => value + 1), []);

  useEffect(() => {
    let alive = true;

    setLoading(true);

    Promise.all([CabinetProfileApi.getPlaces(), CabinetProfileApi.getConsent()])
      .then(([nextPlaces, nextConsent]) => {
        if (!alive) return;
        setPlaces(nextPlaces);
        setConsent(nextConsent);
        setError(null);
        markUpdated();
      })
      .catch(() => {
        if (!alive) return;
        // Профиль не пропадает: личные данные уже есть в store, места и
        // согласие показывают «не удалось» с повтором.
        markFailed();
        setError("Не удалось загрузить места и согласие. Попробуйте ещё раз.");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [version, reloadKey, markUpdated, markFailed]);

  const accountState = user?.accountState;
  const displayName = useMemo(() => formatFullName(user), [user]);

  return (
    <section className="cabinet-page" aria-labelledby="cabinet-profile-title">
      <header className="cabinet-page__head">
        <h1 id="cabinet-profile-title">Профиль</h1>
        <p>
          {displayName} · {accountStateTitle(accountState)}
        </p>
      </header>

      <PersonalDataSection />

      <PlacesSection
        places={places}
        loading={loading}
        error={error}
        onRetry={reload}
      />

      <PasswordSection />

      <ConsentSection
        consent={consent}
        loading={loading}
        error={error}
        onRetry={reload}
      />

      <DeletionSection accountState={accountState} />
    </section>
  );
}

export default observer(CabinetProfilePage);
